// DS2.13
import React, { useId, PropsWithChildren } from 'react';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import { SolidButtonProps } from '../../../Buttons/SolidButton';
import ArrowDown from '../../../Icons/24x24/ArrowDown';
import { IconSize } from '../../../Icons/IconWrap';
import { useDropdownItems } from '../../hooks/useDropdownItems';
import { PopperDropdownManager } from '../../PopperDropdownLayout';
import { NullableDropdownItem } from '../../types';
import StandardDropdownMenu from '../StandardDropdown/StandardDropdownMenu';
import PlainButtonOrLink from '../../../Buttons/PlainButtonOrLink';
import PopperTransparentToggleButton from '../../toggles/PopperTransparentToggleButton';

import s from './BrandedSplitButton.scss';

export interface Props extends Pick<SolidButtonProps, 'loading' | 'disabled'> {
  primaryButton: Omit<
    SolidButtonProps,
    'size' | 'variant' | 'loading' | 'children'
  >;
  dropdownItems: NullableDropdownItem[];
  openOnHover?: boolean;
  menuScrollable?: boolean;
}

const BrandedSplitButton: React.FC<PropsWithChildren<Props>> = React.memo(
  ({
    primaryButton,
    dropdownItems,
    openOnHover,
    loading,
    disabled,
    children,
    menuScrollable,
  }) => {
    const toggleId = useId();
    const dropdownId = useId();
    const { filteredItems } = useDropdownItems(dropdownItems);

    return (
      <div
        className={cn(
          s['root'],
          loading && s['loading'],
          disabled && s['disabled']
        )}
      >
        <PlainButtonOrLink
          className={cn(
            s['primary'],
            disabled && s['disabled'],
            loading && s['loading']
          )}
          {...primaryButton}
        >
          <span>{children}</span>
        </PlainButtonOrLink>

        <div className={s['label']}>
          <span>{children}</span>
        </div>

        <div className={s['divider']} />

        <PopperDropdownManager openOnHover={openOnHover}>
          <PopperTransparentToggleButton
            id={toggleId}
            aria-haspopup="menu"
            aria-controls={dropdownId}
            className={s['secondary']}
            size="large"
            loading={loading}
            disabled={disabled}
          >
            <ArrowDown
              className={s['arrow-icon']}
              size={IconSize.MEDIUM}
              disableDefaultColors
            />
          </PopperTransparentToggleButton>

          <StandardDropdownMenu
            id={dropdownId}
            role="menu"
            aria-labelledby={toggleId}
            menuScrollable={menuScrollable}
            items={filteredItems}
            placement="bottom"
            offsetModifier={
              openOnHover
                ? [
                    0, 0,
                  ] /* no gap - to not hide the menu when mouse travels from the toggle to the menu */
                : undefined
            }
          />
        </PopperDropdownManager>
      </div>
    );
  },
  isEqual
);
BrandedSplitButton.displayName = 'BrandedSplitButton';

export default BrandedSplitButton;
